/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export type ServiceIconType = "preset" | "url";

export interface Service {
  id: number;
  name: string;
  url: string;
  iconType: ServiceIconType;
  iconValue?: string;
  groupName?: string;
  sortOrder: number;
  enabled: boolean;
  showLabel: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceStatus {
  online: boolean;
  statusCode?: number;
}

export interface CreateServiceRequest {
  name: string;
  url: string;
  iconType: ServiceIconType;
  iconValue?: string;
  groupName?: string;
  sortOrder?: number;
  enabled: boolean;
  showLabel: boolean;
}

export interface UpdateServiceRequest extends CreateServiceRequest {
  id: number;
}

async function parseErrorOr(response: Response, fallback: string): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || fallback);
}

export async function getServices(): Promise<Service[]> {
  const response = await fetch(getApiUrl("/services"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch services");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getService(id: number): Promise<Service> {
  const response = await fetch(getApiUrl(`/services/${id}`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch service");
  return response.json();
}

export async function createService(
  data: CreateServiceRequest,
): Promise<Service> {
  const response = await fetch(getApiUrl("/services"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to create service");
  return response.json();
}

export async function updateService(
  id: number,
  data: UpdateServiceRequest,
): Promise<Service> {
  const response = await fetch(getApiUrl(`/services/${id}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to update service");
  return response.json();
}

export async function deleteService(id: number): Promise<void> {
  const response = await fetch(getApiUrl(`/services/${id}`), {
    method: "DELETE",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to delete service");
}

export async function checkServiceStatus(id: number): Promise<ServiceStatus> {
  const response = await fetch(getApiUrl(`/services/${id}/status`));
  if (!response.ok) return parseErrorOr(response, "Failed to check service status");
  return response.json();
}

// service ID -> uptime % (0-100) over the last 30 days. Services with no
// history yet are simply absent from the map.
export async function getServiceUptime(): Promise<Record<number, number>> {
  const response = await fetch(getApiUrl("/services/uptime"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch service uptime");
  return response.json();
}
